<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { h } from 'vue';
import { useVueTable, getCoreRowModel } from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PlacementActions from './PlacementActions.vue';
import ReceiptFiles from './ReceiptFiles.vue';
import {
  userLabel,
  periodLabel,
  moneyLabel,
  paymentDateLabel,
  statusLabels,
} from './list';
const props = defineProps({
  rows: { type: Array, required: true },
  locked: Boolean,
  accountId: { type: [String, Number], required: true },
});
const emit = defineEmits([
  'details',
  'identity',
  'revoke',
  'history',
  'payment',
  'receipt',
  'denied',
]);
function actions(row) {
  return [
    { action: 'details', value: 'details', label: 'Подробности' },
    { action: 'history', value: 'history', label: 'История публикаций' },
    ...(row.paid_amount_minor > 0
      ? [{ action: 'payment', value: 'payment', label: 'Изменить дату оплаты' }]
      : []),
    ...(!row.telegram_user_id && row.status !== 'revoked'
      ? [{ action: 'identity', value: 'identity', label: 'Указать TG User ID' }]
      : []),
    ...(row.status !== 'revoked'
      ? [{ action: 'revoke', value: 'revoke', label: 'Отключить размещение' }]
      : []),
  ];
}
function action(item, row) {
  if (item.action === 'identity') emit('identity', row);
  else if (item.action === 'history') emit('history', row);
  else if (item.action === 'revoke') emit('revoke', row);
  else if (item.action === 'payment') emit('payment', row);
  else emit('details', row);
}
const textCell =
  format =>
  ({ row }) =>
    h(
      'div',
      {
        class: 'text-sm text-n-slate-12 break-words cursor-pointer',
        onClick: () => !props.locked && emit('details', row.original),
      },
      format(row.original)
    );
const columns = [
  {
    id: 'user',
    header: 'Пользователь',
    size: 180,
    cell: ({ row }) =>
      h('div', {}, [
        h(Button, {
          label: userLabel(row.original),
          ghost: true,
          slate: true,
          disabled: props.locked,
          class: 'max-w-full !whitespace-normal text-left',
          onClick: () => emit('details', row.original),
        }),
        row.original.username && row.original.telegram_user_id
          ? h(
              'p',
              { class: 'text-xs text-n-slate-11 m-0 break-all' },
              `ID: ${row.original.telegram_user_id}`
            )
          : null,
      ]),
  },
  {
    id: 'group',
    header: 'Группа',
    size: 180,
    cell: textCell(row => row.group_title || 'Группа недоступна'),
  },
  { id: 'period', header: 'Период', size: 170, cell: textCell(periodLabel) },
  {
    id: 'balance',
    header: 'Осталось',
    size: 90,
    cell: textCell(row => `${row.remaining} из ${row.limit_messages}${row.reserved_messages ? ` · на проверке: ${row.reserved_messages}` : ''}`),
  },
  { id: 'payment', header: 'Оплатил', size: 120, cell: textCell(moneyLabel) },
  {
    id: 'payment_date',
    header: 'Дата оплаты',
    size: 140,
    cell: textCell(paymentDateLabel),
  },
  {
    id: 'status',
    header: 'Статус',
    size: 170,
    cell: textCell(row => statusLabels[row.status] || 'Статус неизвестен'),
  },
  {
    id: 'receipts',
    header: 'Чеки',
    size: 170,
    cell: ({ row }) =>
      h(ReceiptFiles, {
        key: `${props.accountId}-${row.original.id}`,
        compact: true,
        accountId: props.accountId,
        grantId: row.original.id,
        receipts: row.original.receipts || [],
        disabled: props.locked,
        onSaved: value => emit('receipt', value),
        onDenied: () => emit('denied'),
      }),
  },
  {
    id: 'actions',
    header: '',
    size: 56,
    cell: ({ row }) =>
      h(PlacementActions, {
        items: actions(row.original),
        label: `Действия: ${userLabel(row.original)}`,
        disabled: props.locked,
        onAction: item => action(item, row.original),
      }),
  },
];
const table = useVueTable({
  get data() {
    return props.rows;
  },
  columns,
  getCoreRowModel: getCoreRowModel(),
  enableSorting: false,
});
</script>

<template>
  <div class="hidden xl:block overflow-x-auto">
    <Table :table="table" fixed type="compact" class="w-full min-w-[1300px]" />
  </div>
  <div class="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-3">
    <article
      v-for="row in rows"
      :key="row.id"
      class="p-4 border border-n-weak rounded-xl min-w-0 flex flex-col gap-3"
    >
      <Button
        :label="userLabel(row)"
        slate
        ghost
        class="!whitespace-normal text-left justify-start max-w-full"
        :disabled="locked"
        @click="emit('details', row)"
      />
      <p
        v-if="row.username && row.telegram_user_id"
        class="text-xs text-n-slate-11 m-0 break-all"
      >
        ID: {{ row.telegram_user_id }}
      </p>
      <p class="text-sm text-n-slate-11 m-0 break-words">
        {{ statusLabels[row.status] }}
      </p>
      <p class="text-sm text-n-slate-12 m-0 break-words">
        {{ row.group_title || 'Группа недоступна' }}
      </p>
      <p class="text-sm text-n-slate-11 m-0">{{ periodLabel(row) }}</p>
      <p class="text-sm text-n-slate-12 m-0">
        Осталось {{ row.remaining }} из {{ row.limit_messages }}
        <template v-if="row.reserved_messages"> · На проверке: {{ row.reserved_messages }}</template> ·
        {{ moneyLabel(row) }}
      </p>
      <p class="text-sm text-n-slate-11 m-0">
        Дата оплаты: {{ paymentDateLabel(row) }}
      </p>
      <ReceiptFiles
        :key="`${accountId}-${row.id}`"
        :account-id="accountId"
        :grant-id="row.id"
        :receipts="row.receipts || []"
        compact
        :disabled="locked"
        @saved="emit('receipt', $event)"
        @denied="emit('denied')"
      />
      <Button
        label="Подробнее"
        slate
        faded
        :disabled="locked"
        @click="emit('details', row)"
      />
    </article>
  </div>
</template>
